/**
 * GA4 adapter (#100 MR2).
 *
 * Sends the SAME canonical funnel events — already validated by the Mixpanel
 * path in analytics.ts — to Google Analytics 4 via gtag.js (web only). No
 * native SDK is wired in MR2; on iOS/Android `gtag` is unavailable so every
 * call here is a safe no-op, which keeps rollback independent and the diff
 * free of native config that lives outside the product diff.
 *
 * Privacy guarantees (must hold even if analytics.ts forgets a guard):
 *  - Loaded ONLY when `EXPO_PUBLIC_GA4_MEASUREMENT_ID` is set AND consent is
 *    granted. Empty measurement id => this module does nothing (rollback).
 *  - Consent Mode is applied: `analytics_storage` is granted only after the
 *    user opts in; on revoke it flips to `denied`, so GA4 drops further hits.
 *  - Receives only already-sanitized properties from analytics.ts (the same
 *    `clean` payload Mixpanel gets), so no extra PII can leak through here.
 *  - We do NOT forward a user_id to GA4 (kept anonymous-first). Only the
 *    allowlisted `user_role` user-property is set.
 *  - No install/cross-device attribution (FA Client ID) is exposed; #90
 *    deep-link attribution is satisfied by capturing utm_* params at entry and
 *    forwarding them as the allowlisted `source`/`campaign` super-properties.
 */

import { Platform } from 'react-native';

// Measurement id is environment-driven. Empty => GA4 disabled everywhere.
// `disabled` is the same explicit off switch analytics.ts honours: `eas.json`
// refuses an empty string in a build profile, so the development and e2e-test
// profiles say `disabled` to override whatever is stored in EAS (#301).
const ANALYTICS_DISABLED = 'disabled';
const rawGa4MeasurementId = process.env.EXPO_PUBLIC_GA4_MEASUREMENT_ID || '';
const GA4_MEASUREMENT_ID =
  rawGa4MeasurementId === ANALYTICS_DISABLED ? '' : rawGa4MeasurementId;

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Shape mirrors AnalyticsProvider in analytics.ts (kept local to avoid an
// import cycle: analytics.ts imports this module).
export interface Ga4ProviderShape {
  readonly name: string;
  track(eventName: string, properties: Record<string, any>): void;
  identify(distinctId: string | number): void;
  setUserProperties(properties: Record<string, any>): void;
  registerSuperProperties(properties: Record<string, any>): void;
  setConsent(granted: boolean): void;
  flush(): void;
}

const isGa4Configured = (): boolean => GA4_MEASUREMENT_ID.length > 0;
const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

const getGtag = (): ((...args: any[]) => void) | null => {
  if (!isWeb()) return null;
  return typeof window.gtag === 'function' ? window.gtag : null;
};

let ga4LoadPromise: Promise<void> | null = null;

/**
 * Inject the gtag.js snippet and configure the GA4 property. Idempotent.
 * Resolves immediately (no-op) when not on web or when no measurement id is
 * configured — this is the independent rollback: remove the env var and GA4
 * never loads, without touching the Mixpanel or funnel paths.
 */
export const initGa4 = (): Promise<void> => {
  if (ga4LoadPromise) return ga4LoadPromise;

  const run = async (): Promise<void> => {
    if (!isWeb() || !isGa4Configured()) return;
    if (typeof window.gtag === 'function') {
      // Already loaded (e.g. HMR). Just (re)apply config + default consent.
      window.gtag('config', GA4_MEASUREMENT_ID);
      return;
    }

    await new Promise<void>((resolve) => {
      const existing = document.getElementById('ga4-init');
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = 'ga4-init';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
      script.onload = () => resolve();
      script.onerror = () => resolve(); // never block the app on a GA4 failure
      document.head.appendChild(script);
    });

    // gtag bootstrap. Default consent is denied until analytics opt-in flips it.
    window.dataLayer = window.dataLayer || [];
    const gtagFn: (...args: any[]) => void = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      (window.dataLayer as any[]).push(arguments as any);
    };
    window.gtag = gtagFn as any;
    gtagFn('js', new Date());
    gtagFn('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      wait_for_update: 0,
    });
    gtagFn('config', GA4_MEASUREMENT_ID, {
      send_page_view: false, // we drive events explicitly from the funnel
      allow_ad_personalization_signals: false,
    });
  };

  ga4LoadPromise = run();
  return ga4LoadPromise;
};

const ga4Track = (eventName: string, properties: Record<string, any>): void => {
  const gtag = getGtag();
  if (!gtag) return;
  // GA4 event names are lowercase snake_case — the canonical names already fit.
  // No remapping to recommended events: keeps a 1:1 audit trail with Mixpanel
  // and avoids inventing monetary `value` fields the contract forbids.
  gtag('event', eventName, properties || {});
};

const ga4Identify = (_distinctId: string | number): void => {
  // Intentionally a no-op: we keep GA4 anonymous-first (no user_id forwarded).
};

const ga4SetUserProperties = (properties: Record<string, any>): void => {
  const gtag = getGtag();
  if (!gtag) return;
  // Only allowlisted, non-PII user-properties are forwarded.
  const clean = Object.fromEntries(
    Object.entries(properties || {}).filter(
      ([key]) => ['user_role'].includes(key) && typeof properties[key] !== 'object',
    ),
  );
  if (Object.keys(clean).length === 0) return;
  gtag('set', 'user_properties', clean);
};

const ga4RegisterSuperProperties = (properties: Record<string, any>): void => {
  const gtag = getGtag();
  if (!gtag) return;
  const clean = Object.fromEntries(
    Object.entries(properties || {}).filter(
      ([key]) =>
        ['platform', 'app_version', 'environment', 'user_role', 'source', 'campaign', 'medium'].includes(
          key,
        ) && typeof properties[key] !== 'object',
    ),
  );
  if (Object.keys(clean).length === 0) return;
  gtag('set', clean);
};

const ga4SetConsent = (granted: boolean): void => {
  const gtag = getGtag();
  if (!gtag) return;
  gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: granted ? 'granted' : 'denied',
  });
};

const ga4Flush = (): void => {
  // gtag batches and flushes automatically; nothing to do.
};

export const ga4Provider: Ga4ProviderShape = {
  name: 'ga4',
  track: ga4Track,
  identify: ga4Identify,
  setUserProperties: ga4SetUserProperties,
  registerSuperProperties: ga4RegisterSuperProperties,
  setConsent: ga4SetConsent,
  flush: ga4Flush,
};

export const isGa4Enabled = (): boolean => isWeb() && isGa4Configured();

// Test-only: drop the cached load promise so each test can re-trigger init.
export const resetGa4ForTests = (): void => {
  ga4LoadPromise = null;
};
